import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  CircularProgress,
  Button,
  Snackbar,
  TextField,
  Alert,
  Typography,
  Stack,
  Divider,
  Box,
} from "@mui/material";

import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import useAuthRecoverStore from "../../stores/authRecover";

const validationItems = [
  {
    name: "uppercase",
    label: "Pelo menos uma letra maiúscula.",
  },
  {
    name: "lowercase",
    label: "Pelo menos uma letra minúscula.",
  },
  {
    name: "specialChar",
    label: "Pelo menos um caractere especial (@#$!%&).",
  },
  {
    name: "number",
    label: "Pelo menos um número.",
  },
  {
    name: "minLength",
    label: "Pelo menos 10 caracteres de comprimento.",
  },
];

const ForgetPassword = () => {
  const navigate = useNavigate();

  const step = useAuthRecoverStore((state) => state.step);
  const result = useAuthRecoverStore((state) => state.result);
  const login = useAuthRecoverStore((state) => state.login);
  const setLogin = useAuthRecoverStore((state) => state.setLogin);
  const checkLogin = useAuthRecoverStore((state) => state.checkLogin);
  const sendCode = useAuthRecoverStore((state) => state.sendCode);
  const checkCode = useAuthRecoverStore((state) => state.checkCode);
  const updatePassword = useAuthRecoverStore((state) => state.updatePassword);
  const nextStep = useAuthRecoverStore((state) => state.nextStep);
  const prevStep = useAuthRecoverStore((state) => state.prevStep);
  const reset = useAuthRecoverStore((state) => state.reset);

  const [snackbar, setSnackbar] = useState(false);
  const [snackbartxt, setSnackbarTxt] = useState("");
  const [progress, setProgress] = useState(false);

  const [selectedOption, setSelectedOption] = useState("email");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [validations, setValidations] = useState({
    uppercase: false,
    lowercase: false,
    specialChar: false,
    number: false,
    minLength: false,
  });

  useEffect(() => reset(), []);

  useEffect(() => {
    if (step === 1 && (!result.phones || result.phones.length === 0)) {
      setSelectedOption("email");
    }
  }, [step, result.phones]);

  const openWarn = (message) => {
    setSnackbarTxt(message);
    setSnackbar(true);
  };

  const handleCheckLogin = async () => {
    if (!login) {
      openWarn("Informe seu login de acesso.");
      return;
    }
    setProgress(true);
    const res = await checkLogin();
    if (!res || !res.user || !res.user.status) {
      openWarn("Usuário não encontrado ou inativo.");
      setProgress(false);
      return;
    }
    setProgress(false);
    nextStep();
  };

  const handleSendCode = async () => {
    setProgress(true);
    const ok = await sendCode(selectedOption);
    setProgress(false);
    if (!ok) {
      openWarn("Não foi possível enviar o código agora.");
      return;
    }
    nextStep();
  };

  const handleCheckCode = async () => {
    if (!code) {
      openWarn("Informe o código recebido.");
      return;
    }
    setProgress(true);
    const ok = await checkCode(parseInt(code, 10));
    setProgress(false);
    if (!ok) {
      openWarn("Código inválido ou expirado.");
      return;
    }
    nextStep();
  };

  const validatePassword = () => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasSpecialChar = /[@#$!%&]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasMinLength = password.length >= 10;

    setValidations({
      uppercase: hasUppercase,
      lowercase: hasLowercase,
      specialChar: hasSpecialChar,
      number: hasNumber,
      minLength: hasMinLength,
    });
  };

  useEffect(() => validatePassword(), [password]);

  const areAllValidationsTrue = () =>
    validations.uppercase &&
    validations.lowercase &&
    validations.specialChar &&
    validations.number &&
    validations.minLength;

  const handleUpdatePassword = async () => {
    if (!areAllValidationsTrue()) {
      openWarn("Verifique os critérios de senha para continuar.");
      return;
    }
    if (!confirm) {
      openWarn("Confirme a nova senha para continuar.");
      return;
    }
    if (password !== confirm) {
      openWarn("A confirmação não confere com a senha informada.");
      return;
    }
    setProgress(true);
    const ok = await updatePassword(password);
    setProgress(false);
    if (!ok) {
      openWarn("Não foi possível atualizar a senha agora.");
      return;
    }
    nextStep();
  };

  return (
    <React.Fragment>
      {step === 0 && (
        <>
          <Typography
            variant="h6"
            component="div"
            fontFamily="Roboto"
            gutterBottom
            sx={{ fontWeight: "400", marginBottom: "0" }}
            color="text.primary"
          >
            Informe seu login de acesso.
          </Typography>
          <TextField
            label="Login"
            variant="outlined"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            color="primary"
            fullWidth={true}
            margin="dense"
            InputLabelProps={{ sx: { color: "text.secondary" } }}
            sx={{
              "& .MuiOutlinedInput-input": { color: "text.primary" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
            }}
          />
          <Button
            fullWidth={true}
            color="primary"
            variant="contained"
            disabled={progress}
            onClick={handleCheckLogin}
            size="large"
            style={{ marginTop: "8px" }}
          >
            {progress && <CircularProgress size={24} />}
            {!progress && "Continuar"}
          </Button>

          <Button
            fullWidth={true}
            color="primary"
            onClick={() => navigate(`/`)}
            size="large"
            style={{ marginTop: "8px" }}
          >
            Voltar
          </Button>
        </>
      )}

      {step === 1 && (
        <>
          <Typography
            variant="h6"
            component="div"
            fontFamily="Roboto"
            gutterBottom
            sx={{ fontWeight: "400", marginBottom: "0" }}
            color="text.primary"
          >
            Onde você quer receber o código?
          </Typography>
          <Typography
            variant="caption"
            component="div"
            gutterBottom
            color="text.secondary"
          >
            E-mails: {result.emails && result.emails.join(", ")}
          </Typography>
          {result.phones && result.phones.length > 0 && (
            <Typography
              variant="caption"
              component="div"
              gutterBottom
              color="text.secondary"
            >
              Telefones: {result.phones.join(", ")}
            </Typography>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1, "& > *": { flex: 1 } }}
          >
            <Button
              variant={selectedOption === "email" ? "contained" : "outlined"}
              onClick={() => setSelectedOption("email")}
            >
              E-mail
            </Button>
            {result.phones && result.phones.length > 0 && (
              <Button
                variant={selectedOption === "sms" ? "contained" : "outlined"}
                onClick={() => setSelectedOption("sms")}
                disabled
              >
                SMS
              </Button>
            )}
          </Stack>
          {result.phones && result.phones.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              SMS temporariamente desativado
            </Typography>
          )}
          <Button
            fullWidth={true}
            color="primary"
            variant="contained"
            disabled={progress}
            onClick={handleSendCode}
            size="large"
            style={{ marginTop: "8px" }}
          >
            {progress && <CircularProgress size={24} />}
            {!progress && "Enviar código"}
          </Button>
          <Button
            fullWidth={true}
            color="primary"
            onClick={prevStep}
            size="large"
            style={{ marginTop: "8px" }}
          >
            Voltar
          </Button>
        </>
      )}

      {step === 2 && (
        <>
          <Typography
            variant="h6"
            component="div"
            fontFamily="Roboto"
            gutterBottom
            sx={{ fontWeight: "400", marginBottom: "0" }}
            color="text.primary"
          >
            Informe o código recebido.
          </Typography>
          <TextField
            label="Código"
            variant="outlined"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            color="primary"
            fullWidth={true}
            margin="dense"
            InputLabelProps={{ sx: { color: "text.secondary" } }}
            sx={{
              "& .MuiOutlinedInput-input": { color: "text.primary" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
            }}
          />
          <Button
            fullWidth={true}
            color="primary"
            variant="contained"
            disabled={progress}
            onClick={handleCheckCode}
            size="large"
            style={{ marginTop: "8px" }}
          >
            {progress && <CircularProgress size={24} />}
            {!progress && "Validar código"}
          </Button>
          <Button
            fullWidth={true}
            color="primary"
            onClick={prevStep}
            size="large"
            style={{ marginTop: "8px" }}
          >
            Voltar
          </Button>
        </>
      )}

      {step === 3 && (
        <>
          <Typography
            variant="h6"
            component="div"
            fontFamily="Roboto"
            gutterBottom
            sx={{ fontWeight: "400", marginBottom: "0" }}
            color="text.primary"
          >
            Defina sua nova senha.
          </Typography>
          <Typography
            variant="caption"
            component="div"
            gutterBottom
            sx={{ fontWeight: 300, marginBottom: 1 }}
            color="text.secondary"
          >
            Sua nova senha precisa obedecer aos seguintes critérios:
          </Typography>
          <Stack sx={{ mb: 1 }}>
            <Divider sx={{ my: 1 }} />
            {validationItems.map((row, index) => (
              <Box key={index}>
                <Stack direction="row" spacing={1} justifyContent="start">
                  {password === "" && (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      color="disabled"
                    />
                  )}
                  {password !== "" && validations[row.name] && (
                    <CheckCircleIcon fontSize="small" color="success" />
                  )}
                  {password !== "" && !validations[row.name] && (
                    <CancelIcon fontSize="small" color="error" />
                  )}
                  <Typography
                    variant="caption"
                    textAlign="left"
                    color="text.secondary"
                  >
                    {row.label}
                  </Typography>
                </Stack>
                <Divider sx={{ my: "3px" }} />
              </Box>
            ))}
          </Stack>
          <TextField
            label="Senha"
            variant="outlined"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            color="primary"
            fullWidth={true}
            margin="dense"
            InputLabelProps={{ sx: { color: "text.secondary" } }}
            sx={{
              "& .MuiOutlinedInput-input": { color: "text.primary" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
            }}
          />
          <TextField
            label="Confirme a Senha"
            variant="outlined"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            color="primary"
            fullWidth={true}
            margin="dense"
            InputLabelProps={{ sx: { color: "text.secondary" } }}
            sx={{
              "& .MuiOutlinedInput-input": { color: "text.primary" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
            }}
          />
          <Button
            fullWidth={true}
            color="primary"
            variant="contained"
            disabled={progress}
            onClick={handleUpdatePassword}
            size="large"
            style={{ marginTop: "8px" }}
          >
            {progress && <CircularProgress size={24} />}
            {!progress && "Alterar"}
          </Button>
          <Button
            fullWidth={true}
            color="primary"
            onClick={prevStep}
            size="large"
            style={{ marginTop: "8px" }}
          >
            Voltar
          </Button>
        </>
      )}

      {step === 4 && (
        <>
          <Typography
            variant="h6"
            component="div"
            gutterBottom
            sx={{ fontWeight: 300, marginBottom: 1 }}
            color="text.primary"
          >
            Muito bem!
          </Typography>
          <Typography
            variant="body2"
            component="div"
            gutterBottom
            sx={{ fontWeight: 300, marginBottom: 1 }}
            color="text.secondary"
          >
            Sua senha foi atualizada com sucesso.
          </Typography>
          <Button
            fullWidth={true}
            color="primary"
            variant="contained"
            onClick={() => navigate(`/`)}
            size="large"
            style={{ marginTop: "8px" }}
          >
            Entrar
          </Button>
        </>
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(false)}
      >
        <Alert onClose={() => setSnackbar(false)} severity="warning">
          {snackbartxt}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default ForgetPassword;
